import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Popup } from "@progress/kendo-react-popup";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { setEmailGridData, setEmailType, setSelectedEmailData, setShowEmailDescPopup, setUnreadEmailCount } from "../../../../actions/taskSidebar";
import { icon, label, notifyIcon, number, quote, tooltip } from "../../../../config";
import KendoEditor from "../../../../editor/KendoEditor";
import { lastActivityDateAndTime } from "../../../../helper/common";
import { appendFieldsFromObject, createFormData } from "../../../../helper/formDataHelper";
import { useEmailContentRenderer } from "../../../../shared/hooks/useEmailContentRenderer";
import { searchEmailContacts } from "../../../../shared/services/user.service";
import { getEmailAttachments } from "../../../../shared/services/workflow.services";
import {
  GetEmailDetailsByTask,
  addEmailDraft,
  addSpamDetails,
  deleteEmail,
  deleteEmailDraft,
  deleteEmailReply,
  getAllEmailsForTask,
  getWorkflowEmails,
  sendEmailReply,
  updateEmailDetails,
  updateEmailDraft,
  updateEmailReply,
} from "../../../../shared/services/workflowEmails.service";
import { getNotification } from "../../../../utils/common";
import LinkTaskPopup from "../../../Tasks/LinkTaskPopup";
import "./../Description/descriptionPopup.scss";
import EmailAttachments from "./EmailAttachments";
import EmailRecipientInput from "./EmailRecipientInput";
import { email_icon, email_label } from "./TaskEmailConstant";

/**
 * Email Description Popup component
 * @param {Object} props
 * @param {Function} props.setOffset - Function to reset offset when grid data is refreshed
 * @returns JSX
 * @author Sejal
 */
const EmailDescription = ({ resetScroller }) => {
  const { emailData, showEmailDescPopup, emailGridData, task, emailType } = useSelector((state) => state.taskSidebar);
  const [currentEmailId, setCurrentEmailId] = useState(emailData);
  const { user } = useSelector((state) => state.auth);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [draftFiles, setDraftFiles] = useState([]);
  const viewRef = useRef(null);
  const editableRef = React.useRef(true);
  const editorRef = React.useRef(null);
  const popupRef = useRef(null);
  const descPopupRef = useRef(null);
  const toRecipientRef = useRef(null);
  const ccRecipientRef = useRef(null);
  const recipientSectionRef = useRef(null);
  const dispatch = useDispatch();
  const [draftText, setDraftText] = useState("");
  const [draftId, setDraftId] = useState(null);
  const [showLinkTaskPopup, setShowLinkTaskPopup] = useState(false);
  const linkTaskBtnRef = useRef(null);
  const [toRecipients, setToRecipients] = useState([]);
  const [ccRecipients, setCcRecipients] = useState([]);
  const [showCc, setShowCc] = useState(false);
  const [showEmailErrorModal, setShowEmailErrorModal] = useState(false);
  const [emailErrorDetails, setEmailErrorDetails] = useState({ email: "", field: "" });
  const [toInputValue, setToInputValue] = useState(""); // Track To input field value
  const [ccInputValue, setCcInputValue] = useState(""); // Track CC input field value
  const isDraft = emailType == label.DRAFT_CONSTANT || currentEmailId?.EmailType == email_label.DRAFT_CONSTANT;

  // Use custom hook for email content processing and rendering
  const { sanitizedContent: sanitizedEmailContent, emailContentRef } = useEmailContentRenderer(
    currentEmailId?.OriginalContent,
    emailType == label.DRAFT_CONSTANT || currentEmailId?.EmailType == email_label.DRAFT_CONSTANT,
    false, // Don't remove metadata to preserve all content
    "email-description-popup-styles"
  );

  // Memoize send button title based on toRecipients
  const sendButtonTitle = useMemo(() => {
    return toRecipients?.length == 0 ? "Please add at least one recipient" : "Send";
  }, [toRecipients]);

  /**
   * Handle email validation errors from recipient inputs
   */
  const handleEmailValidationError = (email, field) => {
    setEmailErrorDetails({ email, field });
    setShowEmailErrorModal(true);
  };

  /**
   * Validate email format
   */
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email?.trim());
  };

  /**
   * Validate all recipients before sending
   */
  const validateAllRecipients = () => {
    if (toInputValue.trim()) {
      if (!isValidEmail(toInputValue.trim())) {
        handleEmailValidationError(toInputValue.trim(), email_label.TO);
        return false;
      } else {
        handleEmailValidationError(toInputValue.trim(), email_label.TO);
        return false;
      }
    }

    if (showCc && ccInputValue.trim()) {
      if (!isValidEmail(ccInputValue.trim())) {
        handleEmailValidationError(ccInputValue.trim(), "CC");
        return false;
      } else {
        handleEmailValidationError(ccInputValue.trim(), "CC");
        return false;
      }
    }
    for (const recipient of toRecipients) {
      const email = recipient.Email || recipient.email;
      if (!email || !isValidEmail(email)) {
        handleEmailValidationError(email || "Unknown", email_label.TO);
        return false;
      }
    }

    if (showCc && ccRecipients.length > 0) {
      for (const recipient of ccRecipients) {
        const email = recipient.Email || recipient.email;
        if (!email || !isValidEmail(email)) {
          handleEmailValidationError(email || "Unknown", "CC");
          return false;
        }
      }
    }

    return true;
  };

  // Add ref to track when editor is ready for autofocus
  const editorReadyRef = useRef(false);

  /**
   * Focus the editor when it's mounted and in draft mode
   */
  useEffect(() => {
    if (isDraft && showEmailDescPopup && editorRef?.current && !editorReadyRef?.current) {
      const timer = setTimeout(() => {
        if (editorRef?.current && editorRef?.current?.view) {
          editorRef?.current.view.focus();
          editorReadyRef.current = true;
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isDraft, showEmailDescPopup, editorRef.current]);

  // Reset editor ready flag when component unmounts or draft changes
  useEffect(() => {
    return () => {
      editorReadyRef.current = false;
    };
  }, [isDraft]);

  /**
   * Custom onMount handler to focus the editor when it's ready
   * @param {Object} event - The mount event from Kendo Editor
   */
  const handleEditorMount = (event) => {
    if (isDraft && showEmailDescPopup) {
      // Focus the editor after a small delay to ensure it's fully mounted
      setTimeout(() => {
        if (editorRef?.current && editorRef?.current?.view) {
          editorRef?.current?.view?.focus();
          editorReadyRef.current = true;
        }
      }, 150);
    }
  };

  /**
   *  Sets the Description for the Editor when a emailId is selected
   * @author Sejal
   */
  useEffect(() => {
    (async () => {
      if (emailData && emailData?.id) {
        const emaildata = await dispatch(
          GetEmailDetailsByTask({
            emailThreadId: emailData?.id,
            taskId: task.taskId,
            emailType: emailData?.EmailType,
          })
        );
        const attachments = await getEmailAttachments({ emailRefId: emailData?.id, emailRefType: emailData?.EmailType });
        setAttachments(attachments);
        handleMarkAsUnread(emaildata);
      }
    })();
  }, [emailData, task]);

  useEffect(() => {
    return () => {
      setCurrentEmailId(null);
      setDraftText("");
      setDraftId(null);
      setIsSaving(false);
      setIsSaved(false);
      setToRecipients([]);
      setCcRecipients([]);
      setShowCc(false);
    };
  }, []);

  useEffect(() => {
    if (emailType == label.DRAFT_CONSTANT) {
      setAttachments([]);
    }
  }, [emailType, attachments]);

  /**
   * Handle click outside the editor to save draft
   * @param {Event} e - The click event
   * @author Sejal
   */
  const handleClickOutside = (e) => {
    if (isDraft && draftText?.trim()?.length > 0) {
      const editorElement = descPopupRef.current;
      const recipientSection = recipientSectionRef.current;
      if (editorElement) {
        const isClickInsideEditor = editorElement.contains(e.target);
        const isClickInsideRecipients = recipientSection && recipientSection.contains(e.target);
        const isClickOnSendBtn = e.target.closest(".send-btn");
        const isClickOnDeleteBtn = e.target.closest("#email-delete-btn");
        const isClickOnCancelBtn = e.target.closest("#email-detail-popup-close");
        const isClickOnNextBtn = e.target.closest("#email-next-btn");
        const isClickOnPrevBtn = e.target.closest("#email-prev-btn");
        const isClickOnEmailSuggestions = e.target.closest(".email-suggestions-dropdown");
        const isClickOnEmailChip = e.target.closest(".email-chip");
        const isClickOnRecipientInput = e.target.closest(".email-recipient-input-container");
        const isClickOnCcLink = e.target.closest("#email-cc-link");

        if (
          isClickInsideEditor ||
          isClickInsideRecipients ||
          isClickOnSendBtn ||
          isClickOnDeleteBtn ||
          isClickOnCancelBtn ||
          isClickOnNextBtn ||
          isClickOnPrevBtn ||
          isClickOnEmailSuggestions ||
          isClickOnEmailChip ||
          isClickOnRecipientInput ||
          isClickOnCcLink
        ) {
          return;
        }
        handleSaveDraft();
      }
    }
  };

  // Add click outside listener
  useEffect(() => {
    if (isDraft && showEmailDescPopup && draftText?.trim().length > 0) {
      document.addEventListener("click", handleClickOutside);
      return () => {
        document.removeEventListener("click", handleClickOutside);
      };
    }
  }, [isDraft, showEmailDescPopup, draftText, draftId, toRecipients, ccRecipients, showCc]);

  /**
   * Marks email as spam
   * @param {Event} e - The click event
   * @author Sejal
   */
  const handleMarkAsSpam = async (e) => {
    e.stopPropagation();
    if (currentEmailId?.EmailType == email_label.OUTGOING_EMAIL_CONSTANT || currentEmailId?.EmailType == email_label.COMPOSE_EMAIL_CONSTANT) {
      return;
    }
    const payload = {
      emailDetails: [{ emailId: currentEmailId?.Sender, emailThreadId: currentEmailId?.id, companyId: null }],
      userId: user?.id,
    };
    const spamData = await dispatch(addSpamDetails(payload));
    if (spamData) {
      const remainingEmails = emailGridData.filter((item) => !(currentEmailId.id === item.id));
      getNotification(quote.EMAIL_MARKED_AS_SPAM, notifyIcon.SUCCESS_ICON);
      if (currentEmailId?.limitValue === label.EMAIL_MIN || currentEmailId?.limitValue === label.EMAIL_SINGLE) {
        dispatch(setEmailGridData(remainingEmails));
        handleCancel();
      } else handleBack(remainingEmails);
    }
  };

  /**
   * Marks email as read
   * @param {Object} dataEmail - The email data to mark as read
   * @author Sejal
   */
  const handleMarkAsUnread = async (dataEmail) => {
    if (
      dataEmail?.EmailType == email_label.OUTGOING_EMAIL_CONSTANT ||
      dataEmail?.EmailType == email_label.COMPOSE_EMAIL_CONSTANT ||
      dataEmail?.EmailType == email_label.DRAFT_CONSTANT ||
      emailType == label.DRAFT_CONSTANT ||
      dataEmail?.IsRead == 1
    ) {
      setCurrentEmailId(dataEmail);

      return;
    } else {
      const payload = {
        emailDetails: {
          emailThreadId: dataEmail?.id || emailData?.id,
          isRead: number.ONE,
        },
        userId: user?.id,
      };
      const readDetail = await dispatch(updateEmailDetails(payload));
      if (readDetail) {
        const markedEmails = emailGridData.map((item) => (dataEmail?.id === item?.id ? { ...item, IsRead: number.ONE } : item));
        dispatch(setEmailGridData(markedEmails));
        const updatedEmail = { ...dataEmail, IsRead: number.ONE };
        setCurrentEmailId(updatedEmail);
        // Update unread email count - decrement by 1 if email was previously unread
        if (dataEmail?.IsRead !== number.ONE) {
          const currentUnreadCount = task?.unreadEmailCount || 0;
          const newUnreadCount = Math.max(0, currentUnreadCount - 1);
          dispatch(setUnreadEmailCount(newUnreadCount));
        }
      }
    }
  };

  /**
   * Delete email
   * @author Sejal
   */
  const handleDeleteEmail = async () => {
    const payload = { emailDetails: [{ emailThreadId: currentEmailId?.id, emailRefType: currentEmailId?.EmailType }] };
    let deleteEmailResult;
    if (currentEmailId?.EmailType === email_label.DRAFT_CONSTANT || emailType === email_label.DRAFT_CONSTANT) {
      const payload = { emailDetails: { emailDraftId: currentEmailId?.id } };
      let res = await deleteEmailDraft(payload);
      deleteEmailResult = res?.success;
    } else if (
      currentEmailId?.EmailType === email_label.OUTGOING_EMAIL_CONSTANT ||
      currentEmailId?.EmailType === email_label.COMPOSE_EMAIL_CONSTANT
    ) {
      deleteEmailResult = deleteEmailReply(payload);
    } else {
      deleteEmailResult = deleteEmail(payload);
    }
    if (deleteEmailResult) {
      const response = await dispatch(
        getAllEmailsForTask({
          limit: number.FIVE,
          offset: number.ZERO,
          taskId: task.taskId,
        })
      );
      dispatch(setEmailGridData(response));
      if (resetScroller) {
        resetScroller();
      }
      if (currentEmailId?.EmailType === email_label.DRAFT_CONSTANT || emailType === email_label.DRAFT_CONSTANT) {
        getNotification(label.DRAFT_DISCARDED, notifyIcon.SUCCESS_ICON);
      } else {
        getNotification(label.EMAIL_DELETED_SUCCESSFULLY, notifyIcon.SUCCESS_ICON);
      }
      dispatch(setShowEmailDescPopup(false));
      dispatch(setSelectedEmailData(null));
      setCurrentEmailId(null);
    }
  };

  /**
   * Move to the next email
   * @author Sejal
   */
  const handleNext = async () => {
    const emaildataNext = await dispatch(
      GetEmailDetailsByTask({
        emailThreadId: emailData?.id,
        taskId: task.taskId,
        emailType: emailData?.EmailType,
        next: 1,
      })
    );
    dispatch(setSelectedEmailData(emaildataNext));
    setIsSaving(false);
    setIsSaved(false);
    handleMarkAsUnread(emaildataNext);
  };

  /**
   * Move to the previous email
   * @author Sejal
   */
  const handleBack = async () => {
    setIsSaving(false);
    setIsSaved(false);
    const backEmaildata = await dispatch(
      GetEmailDetailsByTask({
        emailThreadId: emailData?.id,
        taskId: task.taskId,
        previous: 1,
        emailType: emailData?.EmailType,
      })
    );
    handleMarkAsUnread(backEmaildata);
    dispatch(setSelectedEmailData(backEmaildata));
  };

  /**
   * Mark email as read
   * @author Sejal
   */
  const handleMarkAsRead = async () => {
    const payload = {
      emailDetails: {
        emailThreadId: currentEmailId?.id || emailData?.id,
        isRead: number.ZERO,
      },
      userId: user?.id,
    };
    const readDetail = await dispatch(updateEmailDetails(payload));
    if (readDetail) {
      getNotification(label.MARKED_AS_READ, notifyIcon.SUCCESS_ICON);
      const markedEmails = emailGridData.map((item) => (currentEmailId?.id === item?.id ? { ...item, IsRead: number.ZERO } : item));
      // Update unread email count - increment by 1 if email was previously read
      if (currentEmailId?.IsRead === number.ONE) {
        const currentUnreadCount = task?.unreadEmailCount || 0;
        dispatch(setUnreadEmailCount(currentUnreadCount + 1));
      }
      if (currentEmailId?.limitValue == label.EMAIL_MIN || currentEmailId?.limitValue == label.EMAIL_SINGLE) {
        dispatch(setEmailGridData(markedEmails));
        handleCancel();
      } else handleBack(markedEmails);
    } else {
      getNotification(label.ERROR_OCCURED, notifyIcon.ERROR_ICON);
    }
  };

  /**
   * Closes the popup when cancel button is clicked
   * @author Sejal
   */
  const handleCancel = () => {
    if (emailType == label.DRAFT_CONSTANT) {
      handleSaveDraft();
    }
    if (currentEmailId?.EmailType == email_label.DRAFT_CONSTANT) {
      handleSaveDraft();
    }
    dispatch(setShowEmailDescPopup(false));
    dispatch(setSelectedEmailData(null));
    setCurrentEmailId(null);
    setIsSaving(false);
    setIsSaved(false);
    setToRecipients([]);
    setCcRecipients([]);
    setShowCc(false);
  };

  /**
   * Converts a blob URL to a blob object by fetching it
   * @param {string} blobURL - The blob URL to fetch
   * @returns {Promise<Blob>} The blob object
   * @author Sejal
   */
  async function getBlobFromURL(blobURL) {
    const response = await fetch(blobURL);
    const blob = await response.blob();
    return blob;
  }

  /**
   * Converts a blob to a File object with specified filename
   * @param {Blob} blob - The blob to convert
   * @param {string} fileName - The filename for the resulting File
   * @returns {File} The File object created from the blob
   * @author Sejal
   */
  function blobToFile(blob, fileName) {
    // Create a File from the Blob
    const file = new File([blob], fileName, {
      type: blob.type,
      lastModified: Date.now(),
    });
    return file;
  }

  /**
   * Converts a blob URL to a File object
   * @param {string} blobURL - The blob URL to convert
   * @param {string} fileName - The filename for the resulting File
   * @returns {Promise<File>} The File object created from the blob URL
   * @author Sejal
   */
  async function convertBlobURLToFile(blobURL, fileName) {
    try {
      // Fetch the Blob from the Blob URL
      const blob = await getBlobFromURL(blobURL);

      // Convert the Blob to a File
      const file = blobToFile(blob, fileName);

      return file;
    } catch (error) {
      console.error("Error converting Blob URL to File:", error);
    }
  }

  /**
   * Safely parse JSON string or return null if invalid
   * @param {string|object} data - The data to parse
   * @returns {Array|null} Parsed array or null
   */
  const safeParseArray = (data) => {
    if (!data) return null;
    if (Array.isArray(data)) return data;
    if (typeof data === "string") {
      const trimmed = data.trim();
      if (trimmed === "" || trimmed === "null" || trimmed === "[]") return null;
      if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
        const parsed = JSON.parse(trimmed);
        return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
      }
    }
    return null;
  };

  /**
   * Parse comma-separated email string into recipient objects
   * @param {string} emailString - Comma-separated email addresses
   * @returns {Array} Array of recipient objects
   */
  const parseEmailString = (emailString) => {
    if (!emailString || emailString.trim() === "" || emailString === "null") return [];
    return emailString.split(",").map((email) => ({
      Name: email.trim(),
      Email: email.trim(),
      isNew: 1,
    }));
  };

  /**
   * Transform recipients array for API payload
   * @param {Array} recipients - Array of recipient objects
   * @returns {Array} Transformed array with isNew flag for manually typed emails
   */
  const transformRecipientsForPayload = (recipients) => {
    return recipients.map((recipient) => {
      if (recipient.isNew) {
        return {
          Name: recipient.Email || recipient.email,
          Email: recipient.Email || recipient.email,
          isNew: 1,
        };
      }
      return recipient;
    });
  };

  /**
   * Extract email addresses as comma-separated string from recipients
   * @param {Array} recipients - Array of recipient objects
   * @returns {string|null} Comma-separated email string or null
   */
  const extractEmailString = (recipients) => {
    return recipients.length > 0 ? recipients.map((recipient) => recipient.Email || recipient.email).join(",") : null;
  };

  const handleEditDraft = async () => {
    setIsSaving(true);
    setIsSaved(false);

    const receiverEmail = extractEmailString(toRecipients);
    const ccEmail = extractEmailString(ccRecipients);
    const toArray = transformRecipientsForPayload(toRecipients);
    const ccArray = transformRecipientsForPayload(ccRecipients);

    const payload = {
      body: draftText || "",
      emailDraftId: draftId ? draftId : currentEmailId?.id,
    };
    if (receiverEmail) {
      payload.receiver = receiverEmail;
    }
    if (toArray.length > 0) {
      payload.toArray = JSON.stringify(toArray);
    }
    if (ccEmail) {
      payload.cc = ccEmail;
    }
    if (ccArray.length > 0) {
      payload.ccArray = JSON.stringify(ccArray);
    }

    const draftData = await dispatch(updateEmailDraft(payload));
    if (draftData) {
      const response = await dispatch(
        getAllEmailsForTask({
          limit: number.FIVE,
          offset: number.ZERO,
          taskId: task.taskId,
        })
      );
      if (resetScroller) {
        resetScroller();
      }
      getNotification(label.DRAFT_SAVED_SUCCESSFULLY, notifyIcon.SUCCESS_ICON);
      dispatch(setEmailGridData(response));
      if (emailType !== label.DRAFT_CONSTANT) {
        const emaildata = await dispatch(
          GetEmailDetailsByTask({
            emailThreadId: currentEmailId?.id,
            taskId: task.taskId,
            emailType: currentEmailId?.EmailType,
          })
        );
        setCurrentEmailId(emaildata);
      }
      setIsSaving(false);
      setIsSaved(true);
      return;
    } else {
      getNotification(label.ERROR_OCCURED, notifyIcon.ERROR_ICON);
      setIsSaving(false);
      setIsSaved(false);
    }
  };
  const handleSendDraft = async () => {
    setIsSaving(true);
    setIsSaved(false);
    let fileAttachments = [];
    fileAttachments = await Promise.all(
      draftFiles?.map((attachment) => {
        const { AttachmentLink, AttachmentName } = attachment;
        return convertBlobURLToFile(AttachmentLink, AttachmentName);
      })
    );
    const receiverEmail = extractEmailString(toRecipients);
    const ccEmail = extractEmailString(ccRecipients);
    const toArray = transformRecipientsForPayload(toRecipients);
    const ccArray = transformRecipientsForPayload(ccRecipients);

    const payload = {
      body: draftText || "",
      emailRef: currentEmailId?.id,
      userId: user?.id,
      sender: emailData?.senderDraft,
      senderName: emailData?.senderNameDraft,
      receiver: receiverEmail,
      taskId: currentEmailId?.TaskId,
      subject: currentEmailId?.EmailType == email_label.INCOMING_EMAIL_CONSTANT ? `Re: ${currentEmailId?.Subject}` : currentEmailId?.Subject,
      emailRefType: currentEmailId?.EmailType,
      files: fileAttachments ? fileAttachments : [],
      workflowId: currentEmailId?.WorkflowId || null,
      cc: ccEmail,
      toArray: toArray.length > 0 ? JSON.stringify(toArray) : null,
      ccArray: ccArray.length > 0 ? JSON.stringify(ccArray) : null,
    };

    const formData = createFormData();
    appendFieldsFromObject(formData, payload);
    const draftData = await dispatch(addEmailDraft(formData));
    if (draftData) {
      setDraftId(draftData.Id);
      const response = await dispatch(
        getAllEmailsForTask({
          limit: number.FIVE,
          offset: number.ZERO,
          taskId: task.taskId,
        })
      );
      if (resetScroller) {
        resetScroller();
      }
      dispatch(setEmailGridData(response));
      getNotification(label.DRAFT_SAVED, notifyIcon.SUCCESS_ICON);
      setIsSaving(false);
      setIsSaved(true);
      return;
    } else {
      getNotification(label.ERROR_OCCURED, notifyIcon.ERROR_ICON);
      setIsSaving(false);
      setIsSaved(false);
    }
  };

  /**
   * Handle saving draft based on whether draftId exists
   * @author Sejal
   */
  const handleSaveDraft = async () => {
    if (draftId) {
      await handleEditDraft();
    } else {
      await handleSendDraft();
    }
  };

  /**
   * Flag the email
   * @param {Event} e - The click event
   * @author Sejal
   */
  const flagEmail = async (e) => {
    e.stopPropagation();
    const payload = {
      emailDetails: {
        emailThreadId: currentEmailId?.id || emailData?.id,
        flag: Number(currentEmailId.Flag) === number.ONE ? 0 : 1,
        emailRefType: currentEmailId?.EmailType,
      },
      userId: user?.id,
    };
    if (currentEmailId?.EmailType === email_label.OUTGOING_EMAIL_CONSTANT || currentEmailId?.EmailType === email_label.COMPOSE_EMAIL_CONSTANT) {
      await dispatch(updateEmailReply(payload));
    } else {
      await dispatch(updateEmailDetails(payload));
    }
    const updatedGridData = emailGridData.map((item) =>
      item.id === currentEmailId?.id ? { ...item, Flag: item.Flag == number.ONE ? number.ZERO : number.ONE } : item
    );
    dispatch(setEmailGridData(updatedGridData));
    setCurrentEmailId((prev) => ({
      ...prev,
      Flag: prev.Flag == number.ONE ? 0 : 1,
    }));
  };

  /**
   * Show more info popup
   * @param {Event} e - The click event
   * @author Sejal
   */
  const handleDrafts = async (e) => {
    e.stopPropagation();
    setAttachments([]);
    dispatch(setEmailType("draft"));
    const response = await dispatch(getWorkflowEmails({ workflowId: currentEmailId?.WorkflowId }));
    const updatedDataItem = {
      ...emailData,
      senderNameDraft: response?.[0]?.WorkflowName || "",
      senderDraft: response?.[0]?.EmailId || "",
    };
    dispatch(setSelectedEmailData(updatedDataItem));
    const parsedToArray = safeParseArray(currentEmailId?.ToArray);
    if (parsedToArray) {
      setToRecipients(parsedToArray);
    } else if (currentEmailId?.EmailType == email_label.INCOMING_EMAIL_CONSTANT && currentEmailId?.Sender) {
      const senderContacts = await searchEmailContacts(currentEmailId.Sender);
      if (senderContacts && senderContacts.length > 0) {
        setToRecipients([senderContacts[0]]);
      } else {
        setToRecipients([{ Name: currentEmailId.Sender, Email: currentEmailId.Sender, isNew: 1 }]);
      }
    } else if (
      (currentEmailId?.EmailType == email_label.OUTGOING_EMAIL_CONSTANT || currentEmailId?.EmailType == email_label.COMPOSE_EMAIL_CONSTANT) &&
      currentEmailId?.Receiver
    ) {
      const receiverContacts = await searchEmailContacts(currentEmailId.Receiver);
      if (receiverContacts && receiverContacts.length > 0) {
        setToRecipients([receiverContacts[0]]);
      } else {
        setToRecipients([{ Name: currentEmailId.Receiver, Email: currentEmailId.Receiver, isNew: 1 }]);
      }
    }
  };

  useEffect(() => {
    (async () => {
      //for already saved draft email details
      if (currentEmailId?.EmailType == email_label.DRAFT_CONSTANT && emailType !== label.DRAFT_CONSTANT) {
        setDraftText(currentEmailId?.OriginalContent);
        setDraftId(currentEmailId?.id);
        const parsedToArray = safeParseArray(currentEmailId?.ToArray);
        if (parsedToArray) {
          setToRecipients(parsedToArray);
        } else {
          const toEmails = parseEmailString(currentEmailId?.Receiver);
          setToRecipients(toEmails);
        }
        const parsedCcArray = safeParseArray(currentEmailId?.CcArray);
        if (parsedCcArray) {
          setCcRecipients(parsedCcArray);
          setShowCc(true);
        } else {
          const ccEmails = parseEmailString(currentEmailId?.CC);
          if (ccEmails.length > 0) {
            setCcRecipients(ccEmails);
            setShowCc(true);
          } else {
            setCcRecipients([]);
            setShowCc(false);
          }
        }
      } else if (emailType == label.DRAFT_CONSTANT) {
        const parsedToArray = safeParseArray(currentEmailId?.ToArray);
        if (parsedToArray) {
          setToRecipients(parsedToArray);
        } else if (currentEmailId?.EmailType == email_label.INCOMING_EMAIL_CONSTANT && currentEmailId?.Sender) {
          const senderContacts = await searchEmailContacts(currentEmailId.Sender);
          if (senderContacts && senderContacts.length > 0) {
            setToRecipients([senderContacts[0]]);
          } else {
            setToRecipients([{ Name: currentEmailId.Sender, Email: currentEmailId.Sender, isNew: 1 }]);
          }
        } else if (
          (currentEmailId?.EmailType == email_label.OUTGOING_EMAIL_CONSTANT || currentEmailId?.EmailType == email_label.COMPOSE_EMAIL_CONSTANT) &&
          currentEmailId?.Receiver
        ) {
          const receiverContacts = await searchEmailContacts(currentEmailId.Receiver);
          if (receiverContacts && receiverContacts.length > 0) {
            setToRecipients([receiverContacts[0]]);
          } else {
            setToRecipients([{ Name: currentEmailId.Receiver, Email: currentEmailId.Receiver, isNew: 1 }]);
          }
        }
      } else {
        setToRecipients([]);
        setCcRecipients([]);
        setShowCc(false);
        setDraftText("");
        setDraftId(null);
      }
    })();
  }, [currentEmailId, emailType, emailData]);

  /**
   * Opens description in edit mode
   * @param None
   * @return None
   * @author Shivam
   */
  const toggleEditable = () => {
    setDraftText(draftText);
  };

  const handleSendEmail = async () => {
    if (!toRecipients || toRecipients.length === 0) {
      getNotification("Please add at least one recipient", notifyIcon.ERROR_ICON);
      return;
    }

    // Validate all recipients before sending
    if (!validateAllRecipients()) {
      return; // Error modal will be shown
    }

    let fileAttachments = [];
    fileAttachments = await Promise.all(
      draftFiles?.map((attachment) => {
        const { AttachmentLink, AttachmentName } = attachment;
        return convertBlobURLToFile(AttachmentLink, AttachmentName);
      })
    );

    const receiverEmail =
      extractEmailString(toRecipients) ||
      (emailType == label.DRAFT_CONSTANT
        ? currentEmailId?.EmailType == email_label.INCOMING_EMAIL_CONSTANT
          ? currentEmailId?.Sender
          : currentEmailId?.Receiver
        : currentEmailId?.Receiver);

    const ccEmail = extractEmailString(ccRecipients);
    const toArray = transformRecipientsForPayload(toRecipients);
    const ccArray = transformRecipientsForPayload(ccRecipients);

    const payload = {
      body: draftText || "",
      emailRefType: currentEmailId?.EmailType == email_label.DRAFT_CONSTANT ? currentEmailId?.EmailRefType : currentEmailId?.EmailType,
      emailRef: emailType == label.DRAFT_CONSTANT ? currentEmailId?.id : currentEmailId?.EmailRef,
      userId: user?.id,
      subject:
        emailType == label.DRAFT_CONSTANT
          ? currentEmailId?.EmailType == email_label.INCOMING_EMAIL_CONSTANT
            ? `Re: ${currentEmailId?.Subject}`
            : currentEmailId?.Subject
          : currentEmailId?.Subject,
      sender: emailType == label.DRAFT_CONSTANT ? emailData?.senderDraft : currentEmailId?.Sender,
      senderName: emailType == label.DRAFT_CONSTANT ? emailData?.senderNameDraft : currentEmailId?.SenderName,
      receiver: receiverEmail,
      files: emailType == label.DRAFT_CONSTANT && fileAttachments ? fileAttachments : [],
      emailDraftId: currentEmailId?.EmailType == email_label.DRAFT_CONSTANT && currentEmailId?.id ? currentEmailId.id : null,
      threadId: currentEmailId?.EmailType == email_label.DRAFT_CONSTANT && currentEmailId?.ThreadId ? currentEmailId.ThreadId : null,
      workflowId: currentEmailId?.EmailType == email_label.DRAFT_CONSTANT && currentEmailId?.WorkflowId ? currentEmailId.WorkflowId : null,
      taskId: currentEmailId?.EmailType == email_label.DRAFT_CONSTANT && currentEmailId?.TaskId ? currentEmailId.TaskId : null,
      cc: ccEmail,
      toArray: toArray.length > 0 ? JSON.stringify(toArray) : null,
      ccArray: ccArray.length > 0 ? JSON.stringify(ccArray) : null,
    };

    const formData = createFormData();
    appendFieldsFromObject(formData, payload);
    const sendEmailResult = await dispatch(sendEmailReply(formData));
    if (sendEmailResult) {
      const response = await dispatch(
        getAllEmailsForTask({
          limit: number.FIVE,
          offset: number.ZERO,
          taskId: task.taskId,
        })
      );
      if (resetScroller) {
        resetScroller();
      }
      dispatch(setEmailGridData(response));
      getNotification(label.EMAIL_SENT_SUCCESSFULLY, notifyIcon.SUCCESS_ICON);
      const emaildata = await dispatch(
        GetEmailDetailsByTask({
          emailThreadId: sendEmailResult?.ReplyId,
          taskId: task.taskId,
          emailType: email_label.OUTGOING_EMAIL_CONSTANT,
        })
      );
      dispatch(setEmailType(""));
      dispatch(setSelectedEmailData(emaildata));
      setCurrentEmailId(emaildata);
      setToRecipients([]);
    } else {
      getNotification(label.ERROR_OCCURED, notifyIcon.ERROR_ICON);
    }
  };
  /**
   * Adds uploaded attachments to the draft files state
   * @param {Array} uploadedAttachments - Array of attachment objects to add
   * @author Sejal
   */
  const addAttachments = (uploadedAttachments) => {
    setDraftFiles((prev) => [...prev, ...uploadedAttachments]);
  };

  return (
    <div>
      <Popup show={showEmailDescPopup} className='desc-popup-container ' popupClass={"desc-popup-child"} animate={false} ref={popupRef}>
        <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
          <div className='dt-popup-header task-email-header d-flex justify-content-between align-items-center pt-1 pb-1 pl-3 pr-3'>
            <div className='dt-popup-title email-detail-header-text'>
              {emailType == label.DRAFT_CONSTANT || currentEmailId?.EmailType == email_label.DRAFT_CONSTANT ? label.EMAIL : label.EMAIL_DETAILS}
              {isDraft && <span className='text-size ml-2'>{label.DRAFTS}</span>}
            </div>
            <div>
              {!isDraft && (
                <span className={` mr-1 cursor-pointer mr-2`} id='email-reply-btn' title={label.REPLY} onClick={handleDrafts}>
                  {email_icon.REPLY}
                </span>
              )}

              {!isDraft && (
                <>
                  {currentEmailId?.Flag == number.ONE ? (
                    <span
                      className={`material-symbols-fill filled-icon cursor-pointer  mr-2 `}
                      id='team-email-flag-btn'
                      title={tooltip.UNFLAG}
                      onClick={flagEmail}>
                      {icon.FLAG_2}
                    </span>
                  ) : (
                    <span className={` black cursor-pointer mr-2 `} id='mark-team-email-flag-btn' title={tooltip.FLAG} onClick={flagEmail}>
                      {icon.FLAG_2}
                    </span>
                  )}
                  {currentEmailId?.IsSpam === 1 ? (
                    <button
                      className='bg-transparent border-none'
                      id='email-spam-btn'
                      disabled={
                        currentEmailId?.EmailType === email_label.OUTGOING_EMAIL_CONSTANT ||
                        currentEmailId?.EmailType === email_label.COMPOSE_EMAIL_CONSTANT
                      }>
                      <span
                        className='red mr-2'
                        id='mark-team-email-spam-btn-all'
                        title={
                          currentEmailId?.EmailType === email_label.OUTGOING_EMAIL_CONSTANT ||
                          currentEmailId?.EmailType === email_label.COMPOSE_EMAIL_CONSTANT
                            ? email_label.DISABLED
                            : email_label.MARKED_AS_SPAM
                        }>
                        {icon.REPORT}
                      </span>
                    </button>
                  ) : (
                    <button
                      className='bg-transparent border-none'
                      id='email-spam-btn'
                      disabled={
                        currentEmailId?.EmailType === email_label.OUTGOING_EMAIL_CONSTANT ||
                        currentEmailId?.EmailType === email_label.COMPOSE_EMAIL_CONSTANT
                      }>
                      <span
                        className='cursor-pointer mr-2'
                        id='mark-team-email-spam-btn-all'
                        title={
                          currentEmailId?.EmailType === email_label.OUTGOING_EMAIL_CONSTANT ||
                          currentEmailId?.EmailType === email_label.COMPOSE_EMAIL_CONSTANT
                            ? email_label.DISABLED
                            : tooltip.MARK_AS_SPAM
                        }
                        onClick={handleMarkAsSpam}>
                        {icon.REPORT}
                      </span>
                    </button>
                  )}
                </>
              )}

              {isDraft && isSaving && <span className='saved-text'>{icon.AUTO_SAVE} Saving</span>}
              {isDraft && isSaved && <span className='saved-text'>Saved</span>}
              {isDraft && (
                <button
                  className='send-btn border-none mr-2 btn'
                  title={sendButtonTitle}
                  onClick={handleSendEmail}
                  disabled={toRecipients.length === 0}>
                  {icon.SEND_EMAILS}
                  <span className='ml-2 mt-3'>Send</span>
                </button>
              )}

              {emailType !== label.DRAFT_CONSTANT && (
                <span className={` mr-2 cursor-pointer`} id='email-delete-btn' title={email_label.DELETE_EMAIL} onClick={handleDeleteEmail}>
                  {icon.DELETE_ICON_EMAIL}
                </span>
              )}
              {!isDraft && (
                <button
                  className='bg-transparent border-none'
                  id='email-mark-read-btn'
                  disabled={
                    currentEmailId?.EmailType === email_label.OUTGOING_EMAIL_CONSTANT ||
                    currentEmailId?.EmailType === email_label.COMPOSE_EMAIL_CONSTANT
                  }>
                  <span
                    className='cursor-pointer'
                    id='email-mark-read-span'
                    title={
                      currentEmailId?.EmailType === email_label.OUTGOING_EMAIL_CONSTANT ||
                      currentEmailId?.EmailType === email_label.COMPOSE_EMAIL_CONSTANT
                        ? email_label.DISABLED
                        : currentEmailId?.IsRead === 1
                        ? "Mark as Unread"
                        : "Acknowledge"
                    }
                    onClick={handleMarkAsRead}>
                    {icon.MARK_EMAIL_UNREAD}
                  </span>
                </button>
              )}
              {emailType !== label.DRAFT_CONSTANT && (
                <>
                  <span className=' cursor-pointer' title={email_label.NEWER}>
                    <button
                      className='email-detail-header bg-transparent border-none '
                      id='email-next-btn'
                      disabled={currentEmailId?.limitValue == "Max" || currentEmailId?.limitValue == label.EMAIL_SINGLE}
                      onClick={handleNext}>
                      {icon.ARROW_BACK_IOS_EMAIL}
                    </button>
                  </span>
                  <span className='cursor-pointer' title={email_label.OLDER}>
                    <button
                      className='email-detail-header bg-transparent border-none'
                      id='email-prev-btn'
                      disabled={currentEmailId?.limitValue == label.EMAIL_MIN || currentEmailId?.limitValue == label.EMAIL_SINGLE}
                      onClick={handleBack}>
                      {" "}
                      {icon.ARROW_FORWARD_IOS}
                    </button>
                  </span>
                </>
              )}

              <span id='email-detail-popup-close' className='dt-popup-cancel-btn' title={label.CLOSE_POPUP} onClick={handleCancel}>
                {icon.CLOSE}
              </span>
            </div>
          </div>
        </Tooltip>
        <div className='email-container-box'>
          <div className='d-flex justify-content-between align-items-center py-1'>
            <div className='ml-3'>
              <strong className='text-size mr-1 '>{email_label.FROM}</strong>
              {emailType == label.DRAFT_CONSTANT ? (
                <span className='text-size'>{emailData?.senderDraft}</span>
              ) : (
                <span className='text-size'>{currentEmailId?.Sender}</span>
              )}
            </div>
            <div className='mr-2 mt-1'>
              {!isDraft &&
                currentEmailId?.EmailType == email_label.INCOMING_EMAIL_CONSTANT &&
                currentEmailId?.IsReadUpdatedByName &&
                currentEmailId?.IsReadUpdatedOn && (
                  <label className='text-size'>
                    {email_label.SEEN_BY} {currentEmailId?.IsReadUpdatedByName} at {lastActivityDateAndTime(currentEmailId?.IsReadUpdatedOn)}
                  </label>
                )}
              {!isDraft &&
                (currentEmailId?.EmailType == email_label.OUTGOING_EMAIL_CONSTANT ||
                  currentEmailId?.EmailType == email_label.COMPOSE_EMAIL_CONSTANT) &&
                currentEmailId?.SenderName &&
                currentEmailId?.CreatedOn && (
                  <label className='text-size'>
                    {email_label.SENT_BY} {currentEmailId?.SenderName} at {lastActivityDateAndTime(currentEmailId?.CreatedOn)}
                  </label>
                )}
            </div>
          </div>
          {isDraft ? (
            <div ref={recipientSectionRef}>
              <div className='border-top'>
                <div className='d-flex align-items-center' ref={toRecipientRef}>
                  <EmailRecipientInput
                    recipients={toRecipients}
                    setRecipients={setToRecipients}
                    label={email_label.TO}
                    placeholder='Add recipients...'
                    onValidationError={handleEmailValidationError}
                    onInputChange={setToInputValue}
                  />
                  {!showCc && (
                    <span
                      id='email-cc-link'
                      className='cursor-pointer text-size ml-2 mr-3'
                      style={{ color: "#007bff", textDecoration: "none" }}
                      onClick={() => setShowCc(true)}>
                      Cc
                    </span>
                  )}
                </div>
              </div>
              {showCc && (
                <div className='border-top' ref={ccRecipientRef}>
                  <EmailRecipientInput
                    recipients={ccRecipients}
                    setRecipients={setCcRecipients}
                    label='CC'
                    placeholder='Add Cc recipients...'
                    onValidationError={handleEmailValidationError}
                    onInputChange={setCcInputValue}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className='d-flex justify-content-between align-items-center py-1 border-top'>
              <div className='ml-3'>
                <strong className='text-size  mr-1'>{email_label.TO}</strong>
                <span className='text-size'>{currentEmailId?.Receiver}</span>
              </div>
              <div className='mr-2 text-size'>
                {currentEmailId?.EmailType !== email_label.OUTGOING_EMAIL_CONSTANT &&
                  currentEmailId?.EmailType !== email_label.COMPOSE_EMAIL_CONSTANT &&
                  currentEmailId?.CreatedOn && (
                    <span className='text-size'>
                      {email_label.RECEIVED_AT} {lastActivityDateAndTime(currentEmailId?.CreatedOn)}
                    </span>
                  )}
                {currentEmailId?.EmailType == email_label.DRAFT_CONSTANT && currentEmailId?.UpdatedBy && (
                  <span className='text-size'>
                    {email_label.MODIFIED_AT} {currentEmailId?.UpdatedBy} At {lastActivityDateAndTime(currentEmailId?.updatedOn)}
                  </span>
                )}
              </div>
            </div>
          )}
          {!isDraft && currentEmailId?.CC && currentEmailId?.CC !== "null" && currentEmailId?.CC.trim() !== "" && (
            <div className='d-flex justify-content-between align-items-center py-1 border-top'>
              <div className='ml-3'>
                <strong className='text-size mr-1'>CC:</strong>
                <span className='text-size'>{currentEmailId?.CC}</span>
              </div>
            </div>
          )}
          <div className='d-flex py-1 justify-content-between align-items-center border-top border-bottom'>
            <div className='d-flex align-items-start ml-3 '>
              <strong className='text-size'>{email_label.SUBJECT}</strong>
              <span className='text-size'>
                {emailType == label.DRAFT_CONSTANT && currentEmailId?.EmailType == email_label.INCOMING_EMAIL_CONSTANT
                  ? `Re: ${currentEmailId?.Subject}`
                  : currentEmailId?.Subject}
              </span>
            </div>
            {emailType !== label.DRAFT_CONSTANT &&
              (currentEmailId?.ProjectTaskId ? (
                <Tooltip anchorElement='target' position='bottom'>
                  <button className='mr-2 link-task' title={"Linked Task"} onClick={(e) => e.stopPropagation()}>
                    {currentEmailId?.ProjectTaskId}
                  </button>
                </Tooltip>
              ) : (
                <>
                  <button className={`btn  mr-2 ${showLinkTaskPopup ? "hide" : ""}`} ref={linkTaskBtnRef} onClick={() => setShowLinkTaskPopup(true)}>
                    Link Task
                  </button>
                  {showLinkTaskPopup && (
                    <LinkTaskPopup
                      anchor={linkTaskBtnRef}
                      showLinkPopUp={showLinkTaskPopup}
                      setShowLinkPopUp={setShowLinkTaskPopup}
                      dataItem={currentEmailId}
                    />
                  )}
                </>
              ))}
          </div>

          {/* Email Attachments */}
          <EmailAttachments
            attachments={emailType === label.DRAFT_CONSTANT ? draftFiles : attachments}
            isDraft={isDraft}
            setAttachments={emailType === label.DRAFT_CONSTANT ? setDraftFiles : setAttachments}
            currentEmailId={currentEmailId}
            addAttachments={addAttachments}
          />

          {currentEmailId?.OriginalContent && !isDraft && (
            <div className='email-description-editor overflow-y-auto overflow-x-hidden p-0 mt-0'>
              {/* Render email HTML directly to preserve all styles and table layouts */}
              {/* <KendoEditor
                desc={currentEmailId.OriginalContent}
                disableToolbar={true}
                viewRef={viewRef}
                editableRef={editableRef}
                editorRef={editorRef}
                editorEditIcon={4}
                showEditIcons={false}
              /> */}
              <div
                ref={emailContentRef}
                className='email-content-wrapper'
                dangerouslySetInnerHTML={{ __html: sanitizedEmailContent }}
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  padding: "10px",
                  // Ensure email content doesn't break out of container
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                }}
              />
            </div>
          )}

          {isDraft && (
            <div className=' overflow-y-auto overflow-x-hidden p-0 mt-0 email-draft' ref={descPopupRef}>
              <KendoEditor
                onMount={handleEditorMount}
                desc={draftText}
                setDesc={setDraftText}
                viewRef={viewRef}
                editableRef={editableRef}
                editorRef={editorRef}
                editorEditIcon={4}
                showEditIcons={false}
                toggleEditable={toggleEditable}
              />
            </div>
          )}
        </div>
      </Popup>

      {/* Email Validation Error Modal */}
      {showEmailErrorModal && (
        <div className='email-error-modal-overlay' onClick={() => setShowEmailErrorModal(false)}>
          <div className='email-error-modal' onClick={(e) => e.stopPropagation()}>
            <div className='email-error-header'>
              <h3 className='email-error-title'>Error</h3>
            </div>
            <div className='email-error-body'>
              <p className='email-error-message'>
                The address "<strong>{emailErrorDetails.email}</strong>" in the "<strong>{emailErrorDetails.field}</strong>" field was not recognized.
                Please make sure that all addresses are properly formed.
              </p>
            </div>
            <div className='email-error-footer'>
              <button
                className='email-error-button'
                onClick={() => setShowEmailErrorModal(false)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Escape") {
                    setShowEmailErrorModal(false);
                  }
                }}
                autoFocus>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailDescription;
